import { Chart as ElementChart } from "react-chartjs-2";
import "chart.js/auto";
import type { ChartData, ChartOptions } from "chart.js";
import { useTranslation } from "react-i18next";
import {
  CapacityMonth,
  capacityMonthLabel,
  capacityRows,
  capacityValue,
} from "../../../../../utils/reports/capacitySeries";
import { useDebugMode } from "../../../../../contexts/DebugModeContext";

interface Props {
  points: CapacityMonth[];
}

export default function CattleMapGraph({ points }: Props) {
  const { t } = useTranslation();
  const { debugEnabled } = useDebugMode();
  const rows = capacityRows(points).slice(0, 2);
  const data: ChartData<"bar" | "line", (number | null)[], string> = {
    labels: points.map(capacityMonthLabel),
    datasets: [
      {
        type: "bar",
        label: t("reports.averageAnimals"),
        backgroundColor: points.map((p) =>
          p.data?.season === "DRY" ? "#D9B43C" : "#62B8AE",
        ),
        data: points.map((p) => p.data?.average_animals ?? null),
        yAxisID: "animals",
        order: 1,
      },
      {
        type: "line",
        label: t("reports.capacity.averageRate"),
        backgroundColor: "#324440",
        borderColor: "#324440",
        data: points.map((p) => p.data?.average_capacity_rate ?? null),
        spanGaps: false,
        clip: false,
        borderWidth: 2,
        yAxisID: "capacity",
        order: 0,
      },
    ],
  };
  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    animation: { duration: 0 },
    layout: {
      padding: { top: 8, bottom: 8, left: 0, right: 0 },
      autoPadding: false,
    },
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          title: (items) =>
            items.length ? capacityMonthLabel(points[items[0].dataIndex]) : "",
          label: () => "",
          afterBody: (items) => {
            if (!items.length) return [];
            const index = items[0].dataIndex;
            const details = rows.map(([label, ...values]) =>
              t("reports.capacity.entry", { label, value: values[index] }),
            );
            if (debugEnabled) {
              const month = points[index].data;
              details.push(
                t("reports.capacity.entry", {
                  label: t("reports.capacity.debugAverageUa"),
                  value: capacityValue(month?.average_ua),
                }),
                t("reports.capacity.entry", {
                  label: t("reports.capacity.debugAverageArea"),
                  value: capacityValue(month?.average_area_hectares),
                }),
              );
            }
            return details;
          },
        },
      },
    },
    scales: {
      x: {
        offset: true,
        title: { display: true, text: t("reports.averageAnimals") },
        ticks: { display: false },
        border: { display: false },
        grid: { offset: true, drawTicks: false },
      },
      animals: { display: false, beginAtZero: true },
      capacity: { display: false, beginAtZero: true },
    },
  };
  return (
    <div className="capacity-chart">
      {!points.some((p) => p.data) && (
        <p className="capacity-chart__empty">{t("reports.capacity.noData")}</p>
      )}
      <ElementChart
        type="bar"
        data={data}
        options={options}
        role="img"
        aria-label={t("reports.stockAndStockingCurve")}
      />
    </div>
  );
}

export function CattleMapLegend() {
  const { t } = useTranslation();
  return (
    <div className="capacity-chart__legend">
      <div className="capacity-chart__seasons">
        <span style={{ background: "#D9B43C" }}>
          {t("reports.capacity.dry")}
        </span>
        <span style={{ background: "#62B8AE" }}>
          {t("reports.capacity.rainy")}
        </span>
      </div>
      <span>{t("dashboardUi.stockingRate")}</span>
      <span className="capacity-chart__line" aria-hidden="true" />
    </div>
  );
}
